import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileText, Pencil, Plus, School, Search, Settings, Trash2, Upload, UserPlus } from 'lucide-react';
import { AdminCreatorType, AdminSchool, adminCreateSchool, adminDeleteSchool, adminGetCreatorTypes, adminGetSchoolStudents, adminGetSchools, adminUpdateSchool, adminGetSchoolGrades, adminCreateGrade, adminUpdateGrade, adminDeleteGrade, adminGetGradeSubjects, adminCreateSubject, adminUpdateSubject, adminDeleteSubject, adminAssignSchoolAdmin, adminGetSchoolAdmins, adminGetUsers, AdminUser, AdminUserBrief, adminGetInstitutionFields, adminGetFieldAcademicYears, AdminField, AdminAcademicYear, AdminStudentBrief, AssignAdminResult } from '../../utils/remote';
import { ADMIN_SCHOOL_PLANS, PLAN_AI_CREDITS, AdminSchoolPlan } from '../../types/admin';
import { useSortableTable, SortColumn } from '../../school_panel/lib/useSortableTable';
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorPanel, FormField, IconButton as UiIconButton, Initials, LoadingState, Modal, PageHeader, PagedFooter, PanelCard, Segmented, SortableTh, Table, Td, Tabs, Th, Track,
} from '../../school_panel/components/ui';
import { useTooltip } from '../../school_panel/components/ui/Tooltip';
import { NAME_MAX, fieldClass } from '../../school_panel/lib/fieldClass';
import { apiErrorText } from '../../school_panel/lib/apiError';
import KpiCard from '../../school_panel/components/ui/KpiCard';
import { initials, lastSeen } from '../../school_panel/pages/dashboards/sections/format';
import { plural } from '../../school_panel/lib/plural';
import CityCombobox from '../../school_panel/components/CityCombobox';

const PAGE_SIZE = 10;
const LOW_ACTIVITY = 50;

/** Share of enrolled students who took an exam in the last 7 days, or null when nobody is enrolled. */
function activityRate(s: AdminSchool): number | null {
  const n = s.student_count ?? 0;
  if (n <= 0) return null;
  return Math.min(100, ((s.active_students_7d ?? 0) / n) * 100);
}

type Kind = 'school' | 'college' | 'university';
// creator_type is the institution type id from the API: 1 school, 2 college, 3 university.
const KIND_BY_TYPE: Record<number, Kind> = { 1: 'school', 2: 'college', 3: 'university' };
function kindOf(s: Pick<AdminSchool, 'creator_type'>): Kind {
  return KIND_BY_TYPE[s.creator_type] ?? 'school';
}

function ActiveBar({ s }: { s: AdminSchool }) {
  const r = activityRate(s);
  if (r === null) return <span className="text-muted">—</span>;
  return (
    <span className="flex items-center gap-2.5">
      <Track className="flex-1" label={`Active this week: ${s.name}`} value={r} tone={r < LOW_ACTIVITY ? 'accent' : 'slate'} />
      <span className="w-9 text-right font-mono font-medium text-ink">{Math.round(r)}%</span>
    </span>
  );
}

type CategoryKey = 'all' | Kind;
const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'school', label: 'School' }, { key: 'college', label: 'College' }, { key: 'university', label: 'University' },
];

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A→Z', key: 'name', dir: 'asc' },
  { value: 'students-desc', label: 'Most students', key: 'students', dir: 'desc' },
  { value: 'activity-asc', label: 'Lowest activity', key: 'activity', dir: 'asc' },
  { value: 'created-desc', label: 'Newest', key: 'created', dir: 'desc' },
] as const;

const num = (n: number) => n.toLocaleString('en-US');

const INPUT = fieldClass;

const ADMIN_ROLES = ['admin', 'school_admin'];
/** Why an existing account can't be linked as a school administrator, or null if it can. */
const cannotLinkReason = (u: AdminUser): string | null => {
  if (u.is_superuser) return `${u.username} is a platform superuser and cannot be a school administrator.`;
  if (u.role && !ADMIN_ROLES.includes(u.role)) {
    return `${u.username} is a ${u.role}${u.institution_name ? ` at ${u.institution_name}` : ''}. Only school administrator accounts can be linked; choose "Create new admin" instead.`;
  }
  return null;
};
/** " They were moved from X, which now has no administrator." when a move happened. */
const movedNote = (r: AssignAdminResult | undefined): string => {
  const p = r?.previous_school;
  if (!p) return '';
  return ` They were moved from ${p.name}${p.admins_left === 0 ? `, which now has no administrator` : ''}.`;
};

const PLAN_LABEL: Record<AdminSchoolPlan, string> = { pilot: 'Pilot', standard: 'Standard', premium: 'Premium' };
const PLAN_TONE: Record<AdminSchoolPlan, string> = {
  premium: 'bg-navy text-on-navy',
  standard: 'bg-mist text-slate',
  pilot: 'bg-warning-bg text-warning-ink',
};
const PLAN_NOTE: Record<AdminSchoolPlan, string> = {
  pilot: 'Free for one term',
  standard: 'all core features',
  premium: 'RAG notes and exam generation',
};
const creditsLabel = (p: AdminSchoolPlan) => `${PLAN_AI_CREDITS[p] / 1000}k AI credits / month`;
// Design copy: the pilot leads with "Free for one term", the others lead with the credits.
const planNote = (p: AdminSchoolPlan) => (p === 'pilot' ? `${PLAN_NOTE[p]} · ${creditsLabel(p)}` : `${creditsLabel(p)} · ${PLAN_NOTE[p]}`);
const PLAN_OPTIONS = ADMIN_SCHOOL_PLANS.map((p) => ({ key: p, label: PLAN_LABEL[p] }));

function PlanPill({ plan }: { plan: AdminSchoolPlan | undefined }) {
  const p = plan && PLAN_LABEL[plan] ? plan : 'standard';
  return <span className={`inline-block rounded-[8px] px-2 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] ${PLAN_TONE[p]}`}>{PLAN_LABEL[p]}</span>;
}

/** Parses the optional override input: '' clears it (null); anything else must be a whole number >= 0. */
function parseOverride(v: string): { ok: true; value: number | null } | { ok: false } {
  const s = v.trim();
  if (!s) return { ok: true, value: null };
  const n = Number(s);
  return Number.isInteger(n) && n >= 0 ? { ok: true, value: n } : { ok: false };
}

const SCHOOL_COLUMNS: SortColumn<AdminSchool>[] = [
  { key: 'id', accessor: (s) => s.id, type: 'number' },
  { key: 'name', accessor: (s) => s.name },
  { key: 'category', accessor: (s) => s.creator_type_name },
  { key: 'students', accessor: (s) => s.student_count ?? 0, type: 'number' },
  { key: 'admins', accessor: (s) => s.admin_count ?? 0, type: 'number' },
  { key: 'activity', accessor: (s) => activityRate(s), type: 'number' },
  { key: 'plan', accessor: (s) => s.plan },
  { key: 'last_activity', accessor: (s) => s.last_activity, type: 'date' },
  { key: 'created', accessor: (s) => s.created, type: 'date' },
];

const SCHOOL_STUDENT_COLUMNS: SortColumn<AdminStudentBrief>[] = [
  { key: 'id', accessor: (s) => s.id, type: 'number' },
  { key: 'name', accessor: (s) => s.name },
  { key: 'grade', accessor: (s) => s.grade },
];

const SCHOOL_ADMIN_COLUMNS: SortColumn<AdminUserBrief>[] = [
  { key: 'user', accessor: (a) => `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim() || a.username },
  { key: 'email', accessor: (a) => a.email ?? '' },
];

const EMPTY_FORM = { name: '', creator_type: '' as number | '', city: '', plan: 'standard' as AdminSchoolPlan, override: '' };
const MIN_PASSWORD = 6;
type FirstAdminMode = 'create' | 'existing';
const EMPTY_FIRST_ADMIN = { mode: 'existing' as FirstAdminMode, username: '', password: '', first_name: '', last_name: '', email: '' };
type AssignPayload = Parameters<typeof adminAssignSchoolAdmin>[1];
// Create mode only: the account is made by the endpoint, so a password is mandatory. (An existing account is
// bound by id from the confirmed lookup, never rebuilt from the form text.)
const newAdminPayload = (fa: typeof EMPTY_FIRST_ADMIN): AssignPayload | null => (fa.mode === 'create' && fa.username.trim() && fa.password.length >= MIN_PASSWORD
  ? { username: fa.username.trim(), password: fa.password, email: fa.email.trim() || undefined, first_name: fa.first_name.trim() || undefined, last_name: fa.last_name.trim() || undefined }
  : null);
type BoundAdmin = { payload: AssignPayload; who: string };
const EMPTY_ADMIN_FORM = { existing_user_id: '', username: '', password: '', email: '', first_name: '', last_name: '' };

type ConfirmState =
  | { kind: 'school'; school: AdminSchool }
  | { kind: 'grade'; id: number; name: string }
  | { kind: 'subject'; id: number; name: string }
  | null;

/** Compact icon-only button with an accessible name and a tooltip that is never clipped by the table. */
function IconButton({ label, onClick, tone = 'neutral', children }: {
  label: string; onClick: (e: React.MouseEvent) => void; tone?: 'neutral' | 'danger'; children: React.ReactNode;
}) {
  const { ref, triggerProps, tooltip } = useTooltip<HTMLButtonElement>(label);
  return (
    <>
      <button
        ref={ref}
        type="button"
        aria-label={label}
        onClick={onClick}
        {...triggerProps}
        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border bg-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          tone === 'danger'
            ? 'border-line-strong text-slate hover:border-danger hover:bg-danger-bg hover:text-danger-ink'
            : 'border-line-strong text-slate hover:bg-surface-2 hover:text-ink'
        }`}>
        {children}
      </button>
      {tooltip}
    </>
  );
}

function InlineError({ children }: { children: React.ReactNode }) {
  return <p role="alert" className="text-xs text-danger-ink">{children}</p>;
}

const AdminSchools: React.FC = () => {
  const qc = useQueryClient();

  // List/search/pagination state
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<CategoryKey>('all');
  const [view, setView] = useState<'table' | 'cards'>('cards');

  // Add-institute form
  const [form, setForm] = useState<{ name: string; creator_type: number | ''; city: string; plan: AdminSchoolPlan; override: string }>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<{ name?: string; creator_type?: string; override?: string }>({});
  const [createdMsg, setCreatedMsg] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [firstAdmin, setFirstAdmin] = useState(EMPTY_FIRST_ADMIN);
  const [firstAdminError, setFirstAdminError] = useState<string | null>(null);
  // An existing account picked as first administrator, awaiting an explicit confirm (it may move schools).
  const [confirmExisting, setConfirmExisting] = useState<AdminUser | null>(null);
  // The school was created but its administrator could not be assigned: kept for a retry.
  const [adminFailure, setAdminFailure] = useState<{ schoolId: number; schoolName: string; payload: AssignPayload; who: string; message: string } | null>(null);
  // The administrator request fixed at submit time (confirmed account id, or the validated new-account payload).
  const boundAdminRef = useRef<BoundAdmin | null>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [adminRetrying, setAdminRetrying] = useState(false);

  // Edit dialog
  const [editing, setEditing] = useState<AdminSchool | null>(null);
  const [editName, setEditName] = useState('');
  const [editType, setEditType] = useState<number | ''>('');
  const [editCity, setEditCity] = useState('');
  const [editPlan, setEditPlan] = useState<AdminSchoolPlan>('standard');
  const [editOverride, setEditOverride] = useState('');
  const [editOverrideError, setEditOverrideError] = useState<string | null>(null);

  // Delete / confirm
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Selected school manage view
  const [selected, setSelected] = useState<AdminSchool | null>(null);
  const [activeTab, setActiveTab] = useState<'students' | 'structure' | 'admins'>('students');
  const [studPage, setStudPage] = useState(1);
  const [studSearch, setStudSearch] = useState('');

  // Grades & subjects state (for schools)
  const [grades, setGrades] = useState<{ id: number; name: string }[]>([]);
  const [gradesError, setGradesError] = useState<string | null>(null);
  const [gradeActionError, setGradeActionError] = useState<string | null>(null);
  const [newGrade, setNewGrade] = useState('');
  const [editingGradeId, setEditingGradeId] = useState<number | null>(null);
  const [editingGradeName, setEditingGradeName] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState<number | null>(null);
  const [subjects, setSubjects] = useState<{ id: number; name: string }[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState<string | null>(null);
  const [subjectActionError, setSubjectActionError] = useState<string | null>(null);
  const [newSubject, setNewSubject] = useState('');
  const [editingSubjectId, setEditingSubjectId] = useState<number | null>(null);
  const [editingSubjectName, setEditingSubjectName] = useState('');

  // Fields & Academic Years state (for universities/colleges)
  const [fields, setFields] = useState<AdminField[]>([]);
  const [fieldsError, setFieldsError] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<number | null>(null);
  const [years, setYears] = useState<AdminAcademicYear[]>([]);
  const [yearsError, setYearsError] = useState<string | null>(null);

  // Assign/create admin state
  const [adminForm, setAdminForm] = useState(EMPTY_ADMIN_FORM);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminMsg, setAdminMsg] = useState<string | null>(null);
  // Linking an administrator of another school, awaiting the operator's OK to move them.
  const [moveConfirm, setMoveConfirm] = useState<{ username: string; from: string } | null>(null);
  const [admins, setAdmins] = useState<AdminUserBrief[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addOpen, setAddOpen] = useState(false);
  // Opens the Add school drawer (the dialog moves focus to its first field).
  const focusAddForm = () => setAddOpen(true);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      try {
        JSON.parse(content); // validate
        setForm((f) => ({ ...f, name: content }));
        setUploadError(null);
        setFormErrors((x) => ({ ...x, name: undefined }));
        focusAddForm();
      } catch {
        setUploadError('That file is not valid JSON. Choose a .json file with a list of institutes.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Ensure admins are reloaded when switching schools or tab is admins
  useEffect(() => {
    let cancelled = false;
    async function loadAdmins() {
      if (selected && activeTab === 'admins') {
        try {
          const list = await adminGetSchoolAdmins(selected.id);
          if (!cancelled) setAdmins(list);
        } catch (e) {
          if (!cancelled) setAdmins([]);
        }
      }
    }
    // clear previous admins on school change
    setAdmins([]);
    loadAdmins();
    return () => { cancelled = true; };
  }, [selected?.id, activeTab]);

  const schoolsQ = useQuery({
    queryKey: ['admin-schools'],
    queryFn: () => adminGetSchools({ page: 1, page_size: 100000 }),
    staleTime: 5_000,
  });

  const typesQ = useQuery({
    queryKey: ['admin-creator-types'],
    queryFn: adminGetCreatorTypes,
  });

  const createMut = useMutation({
    mutationFn: adminCreateSchool,
    onSuccess: async (data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      qc.invalidateQueries({ queryKey: ['admin-ai-credits'] });
      const isBulk = vars.name.trim().startsWith('[');
      const bound = boundAdminRef.current;
      boundAdminRef.current = null;
      setForm(EMPTY_FORM);
      setFirstAdmin(EMPTY_FIRST_ADMIN);
      setFormErrors({});
      setCategory('all');
      if (isBulk) {
        setSearch('');
        setCreatedMsg('Institutes added.');
      } else {
        // Show the new school: filter the directory down to it.
        setSearch(vars.name);
        setCreatedMsg(`Added "${vars.name}". It is shown in the directory.`);
      }
      setPage(1);
      setAddOpen(false);
      // Optional first administrator: the existing assign-admin call, made once the school exists.
      const newId = (data as { id?: unknown } | undefined)?.id;
      if (!isBulk && typeof newId === 'number' && bound) {
        const { payload, who } = bound;
        try {
          const res = await adminAssignSchoolAdmin(newId, payload);
          qc.invalidateQueries({ queryKey: ['admin-schools'] });
          setCreatedMsg(`Added "${vars.name}" with ${who} as administrator.${movedNote(res)}`);
        } catch (err) {
          // The school stays; the failure is shown in red with a retry.
          setCreatedMsg(null);
          setAdminFailure({ schoolId: newId, schoolName: vars.name, payload, who, message: apiErrorText(err, 'unknown error') });
        }
      }
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<{ name: string; creator_type: number; city: string; plan: AdminSchoolPlan; ai_credit_allocation: number | null }> }) => adminUpdateSchool(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      // Plan and override change the allocation shown on the AI usage page.
      qc.invalidateQueries({ queryKey: ['admin-ai-credits'] });
      setEditing(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => adminDeleteSchool(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      qc.invalidateQueries({ queryKey: ['admin-ai-credits'] });
      setSelected((cur) => (cur && cur.id === id ? null : cur));
      setConfirm(null);
      setDeleteError(null);
    },
    onError: (err) => {
      setConfirm(null);
      setDeleteError(apiErrorText(err, 'Could not delete the institute.'));
    },
  });

  const types = typesQ.data || [];
  const schools = useMemo(() => schoolsQ.data?.results ?? [], [schoolsQ.data]);

  // Header and tiles sum the whole directory, never the visible page or filter.
  const totals = useMemo(() => {
    const t = { students: 0, admins: 0, active: 0, low: 0, setup: 0, school: 0, college: 0, university: 0, premium: 0, standard: 0, pilot: 0 };
    schools.forEach((s) => {
      const n = s.student_count ?? 0;
      t.students += n;
      t.admins += s.admin_count ?? 0;
      t.active += s.active_students_7d ?? 0;
      t[kindOf(s)] += 1;
      if (n <= 0) t.setup += 1;
      if (s.plan === 'premium') t.premium += 1;
      else if (s.plan === 'pilot') t.pilot += 1;
      else t.standard += 1;
      const r = activityRate(s);
      if (r !== null && r < LOW_ACTIVITY) t.low += 1;
    });
    return t;
  }, [schools]);
  // The institute with the lowest activity rate, named under the "Low activity" tile.
  const lowest = useMemo(() => {
    let best: { name: string; r: number } | null = null;
    schools.forEach((s) => { const r = activityRate(s); if (r !== null && (best === null || r < best.r)) best = { name: s.name, r }; });
    return best as { name: string; r: number } | null;
  }, [schools]);
  const cityCount = useMemo(() => new Set(schools.map((s) => (s.city ?? '').trim().toLowerCase()).filter(Boolean)).size, [schools]);

  // The API ignores paging and search, so filtering and paging happen here.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return schools.filter((s) => (category === 'all' || kindOf(s) === category)
      && (!q || s.name.toLowerCase().includes(q) || (s.creator_type_name || '').toLowerCase().includes(q)));
  }, [schools, category, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const { rows: sortedAll, sort: schoolSort, toggle: toggleSchoolSort, setSort: setSchoolSort } = useSortableTable(
    filtered,
    SCHOOL_COLUMNS,
    { key: 'name', dir: 'asc' },
  );
  const sortedSchools = sortedAll.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const sortValue = SORT_OPTIONS.find((o) => o.key === schoolSort?.key && o.dir === schoolSort?.dir)?.value ?? '';

  // Back to page 1 whenever the view changes.
  useEffect(() => { setPage(1); }, [category, schoolSort?.key, schoolSort?.dir]);

  // Students query, depends on selected school
  const studentsQ = useQuery({
    queryKey: ['admin-school-students', selected?.id, studPage, studSearch],
    queryFn: () => adminGetSchoolStudents(selected!.id, { page: studPage, page_size: 10, search: studSearch.trim() || undefined }),
    enabled: !!selected,
    placeholderData: keepPreviousData,
  });

  const { rows: sortedStudents, sort: studentSort, toggle: toggleStudentSort } = useSortableTable(
    studentsQ.data?.results,
    SCHOOL_STUDENT_COLUMNS,
    { key: 'name', dir: 'asc' },
  );
  const { rows: sortedAdmins, sort: adminSort, toggle: toggleAdminSort } = useSortableTable(
    admins,
    SCHOOL_ADMIN_COLUMNS,
    { key: 'user', dir: 'asc' },
  );

  const isBulkName = form.name.trim().startsWith('[');

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    setCreatedMsg(null);
    createMut.reset();
    const name = form.name.trim();
    const errs: { name?: string; creator_type?: string; override?: string } = {};
    if (!name) errs.name = 'Name is required';
    else if (!isBulkName && schools.some((s) => s.name.trim().toLowerCase() === name.toLowerCase())) {
      errs.name = 'An institute with this name already exists';
    }
    if (!form.creator_type) errs.creator_type = 'Pick a category';
    const override = parseOverride(form.override);
    if (!override.ok) errs.override = 'Enter a whole number of credits, or leave blank';
    setFormErrors(errs);
    setFirstAdminError(null);
    setAdminFailure(null);
    if (errs.name || errs.creator_type || errs.override || !override.ok) return;
    const fa = firstAdmin;
    boundAdminRef.current = null;
    const wantsAdmin = !isBulkName && (fa.username.trim() || fa.password || fa.email.trim() || fa.first_name.trim() || fa.last_name.trim());
    if (wantsAdmin) {
      if (!fa.username.trim()) { setFirstAdminError('Enter the administrator’s username, or clear the panel.'); return; }
      if (fa.mode === 'create' && fa.password.length < MIN_PASSWORD) { setFirstAdminError(`The temporary password needs at least ${MIN_PASSWORD} characters.`); return; }
      if (fa.mode === 'existing') {
        // Look the account up first: assigning moves it into this school, so the operator confirms who and from where.
        setLookupBusy(true);
        void (async () => {
          try {
            const all = await adminGetUsers({ page: 1, page_size: 100000 });
            const hit = all.results.find((u) => u.username.toLowerCase() === fa.username.trim().toLowerCase());
            if (!hit) { setFirstAdminError(`No account named "${fa.username.trim()}". Check the username or choose "Create new admin".`); return; }
            const why = cannotLinkReason(hit);
            if (why) { setFirstAdminError(why); return; }
            setConfirmExisting(hit);
          } catch (err) {
            setFirstAdminError(apiErrorText(err, 'Could not look that account up.'));
          } finally {
            setLookupBusy(false);
          }
        })();
        return;
      }
      const payload = newAdminPayload(fa);
      if (!payload) { setFirstAdminError(`The temporary password needs at least ${MIN_PASSWORD} characters.`); return; }
      boundAdminRef.current = { payload, who: payload.username ?? fa.username.trim() };
    }
    submitCreate();
  };

  const submitCreate = () => {
    const name = form.name.trim();
    const override = parseOverride(form.override);
    if (!override.ok) return;
    createMut.mutate({
      name,
      creator_type: Number(form.creator_type),
      city: form.city.trim(),
      plan: form.plan,
      ...(override.value !== null ? { ai_credit_allocation: override.value } : {}),
    });
  };

  const retryAssign = async () => {
    if (!adminFailure) return;
    setAdminRetrying(true);
    try {
      const res = await adminAssignSchoolAdmin(adminFailure.schoolId, adminFailure.payload);
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      setCreatedMsg(`Added "${adminFailure.schoolName}" with ${adminFailure.who} as administrator.${movedNote(res)}`);
      setAdminFailure(null);
    } catch (err) {
      setAdminFailure({ ...adminFailure, message: apiErrorText(err, 'unknown error') });
    } finally {
      setAdminRetrying(false);
    }
  };

  const openEdit = (s: AdminSchool) => {
    updateMut.reset();
    setEditing(s);
    setEditName(s.name);
    setEditType(s.creator_type || '');
    setEditCity(s.city ?? '');
    setEditPlan(s.plan ?? 'standard');
    setEditOverride(s.ai_credit_allocation_override != null ? String(s.ai_credit_allocation_override) : '');
    setEditOverrideError(null);
  };

  const submitEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const override = parseOverride(editOverride);
    if (!override.ok) { setEditOverrideError('Enter a whole number of credits, or leave blank'); return; }
    setEditOverrideError(null);
    const patch: { name?: string; creator_type?: number; city?: string; plan?: AdminSchoolPlan; ai_credit_allocation?: number | null } = {
      name: editName.trim() || editing.name,
      city: editCity.trim(),
      plan: editPlan,
      ai_credit_allocation: override.value,
    };
    if (editType) patch.creator_type = Number(editType);
    updateMut.mutate({ id: editing.id, patch });
  };

  const isUniversity = (s: AdminSchool | null) => {
    return !!s && kindOf(s) !== 'school';
  };

  const openManage = (s: AdminSchool) => {
    setSelected(s);
    setActiveTab('students');
    setStudPage(1);
    setStudSearch('');
  };

  const loadGradeSubjects = async (schoolId: number, gradeId: number) => {
    setSelectedGradeId(gradeId);
    setSubjects([]);
    setSubjectsError(null);
    setSubjectActionError(null);
    setSubjectsLoading(true);
    try {
      const subs = await adminGetGradeSubjects(schoolId, gradeId);
      setSubjects(subs);
    } catch (err) {
      setSubjectsError(apiErrorText(err, ''));
    } finally {
      setSubjectsLoading(false);
    }
  };

  const switchTab = async (tab: 'students' | 'structure' | 'admins') => {
    setActiveTab(tab);
    if (tab === 'structure') {
      setSelectedGradeId(null);
      setSubjects([]);
      setSubjectsError(null);
      setSelectedFieldId(null);
      setYears([]);
      setYearsError(null);
      setGradesError(null);
      setFieldsError(null);
      setGradeActionError(null);
      setSubjectActionError(null);
      if (selected) {
        if (isUniversity(selected)) {
          try { setFields(await adminGetInstitutionFields(selected.id)); }
          catch (err) { setFields([]); setFieldsError(apiErrorText(err, 'Could not load fields of study.')); }
        } else {
          try { setGrades(await adminGetSchoolGrades(selected.id)); }
          catch (err) { setGrades([]); setGradesError(apiErrorText(err, 'Could not load grade levels.')); }
        }
      }
    }
    if (tab === 'admins' && selected) {
      setAdminError(null);
      setAdminMsg(null);
      try { setAdmins(await adminGetSchoolAdmins(selected.id)); } catch { /* the effect above also reloads */ }
    }
  };

  const addGrade = async () => {
    if (!selected || !newGrade.trim()) return;
    setGradeActionError(null);
    try {
      const g = await adminCreateGrade(selected.id, newGrade.trim());
      setGrades((prev) => [...prev, g]);
      setNewGrade('');
    } catch (err) {
      setGradeActionError(apiErrorText(err, 'Could not add the grade.'));
    }
  };

  const renameGrade = async (gradeId: number, current: string) => {
    if (!selected) return;
    const name = editingGradeName.trim() || current;
    setEditingGradeId(null);
    if (name === current) return;
    setGradeActionError(null);
    try {
      const up = await adminUpdateGrade(selected.id, gradeId, name);
      setGrades((prev) => prev.map((x) => (x.id === gradeId ? up : x)));
    } catch (err) {
      setGradeActionError(apiErrorText(err, 'Could not rename the grade.'));
    }
  };

  const addSubject = async () => {
    if (!selected || !selectedGradeId || !newSubject.trim()) return;
    setSubjectActionError(null);
    try {
      const s = await adminCreateSubject(selected.id, selectedGradeId, newSubject.trim());
      setSubjects((prev) => [...prev, s]);
      setNewSubject('');
    } catch (err) {
      setSubjectActionError(apiErrorText(err, 'Could not add the subject.'));
    }
  };

  const renameSubject = async (subjectId: number, current: string) => {
    if (!selected) return;
    const name = editingSubjectName.trim() || current;
    setEditingSubjectId(null);
    if (name === current) return;
    setSubjectActionError(null);
    try {
      const up = await adminUpdateSubject(selected.id, subjectId, name);
      setSubjects((prev) => prev.map((x) => (x.id === subjectId ? up : x)));
    } catch (err) {
      setSubjectActionError(apiErrorText(err, 'Could not rename the subject.'));
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    if (confirm.kind === 'school') {
      deleteMut.mutate(confirm.school.id);
      return;
    }
    if (!selected) { setConfirm(null); return; }
    const c = confirm;
    setConfirm(null);
    if (c.kind === 'grade') {
      setGradeActionError(null);
      try {
        await adminDeleteGrade(selected.id, c.id);
        setGrades((prev) => prev.filter((x) => x.id !== c.id));
        if (selectedGradeId === c.id) { setSelectedGradeId(null); setSubjects([]); setSubjectsError(null); }
      } catch (err) {
        setGradeActionError(apiErrorText(err, 'Could not delete the grade.'));
      }
    } else {
      setSubjectActionError(null);
      try {
        await adminDeleteSubject(selected.id, c.id);
        setSubjects((prev) => prev.filter((x) => x.id !== c.id));
      } catch (err) {
        setSubjectActionError(apiErrorText(err, 'Could not delete the subject.'));
      }
    }
  };

  const linkExistingAdmin = async (confirmMove = false) => {
    if (!selected || !adminForm.existing_user_id.trim()) return;
    const username = adminForm.existing_user_id.trim();
    setAdminError(null);
    setAdminMsg(null);
    try {
      const res = await adminAssignSchoolAdmin(selected.id, { username, ...(confirmMove ? { confirm_move: true } : {}) });
      setAdminForm({ ...adminForm, existing_user_id: '' });
      setAdminMsg(res?.already ? `${res.username} is already an administrator of ${selected.name}.` : `${res?.username ?? username} linked as administrator.${movedNote(res)}`);
      qc.invalidateQueries({ queryKey: ['admin-schools'] });
      try { setAdmins(await adminGetSchoolAdmins(selected.id)); } catch { /* keep list */ }
    } catch (err) {
      const data = (err as { response?: { status?: number; data?: { code?: string; current_school?: { name: string } | null } } })?.response;
      if (data?.status === 409 && data.data?.code === 'admin_elsewhere' && data.data.current_school) {
        // Moving someone out of another school is the operator's call: ask first.
        setMoveConfirm({ username, from: data.data.current_school.name });
        return;
      }
      setAdminError(apiErrorText(err, 'Could not link that user.'));
    }
  };

  const createAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setAdminMsg(null);
    if (!adminForm.username.trim()) { setAdminError('Username is required.'); return; }
    setAdminError(null);
    try {
      await adminAssignSchoolAdmin(selected.id, {
        username: adminForm.username.trim(),
        password: adminForm.password || undefined,
        email: adminForm.email || undefined,
        first_name: adminForm.first_name || undefined,
        last_name: adminForm.last_name || undefined,
      });
      setAdminForm(EMPTY_ADMIN_FORM);
      setAdminMsg('Administrator account created and assigned.');
      try { setAdmins(await adminGetSchoolAdmins(selected.id)); } catch { /* keep list */ }
    } catch (err) {
      setAdminError(apiErrorText(err, 'Could not create the administrator.'));
    }
  };

  const confirmTitle = confirm?.kind === 'school' ? 'Delete institute?' : confirm?.kind === 'grade' ? 'Delete grade?' : 'Delete subject?';
  const confirmBody = confirm?.kind === 'school'
    ? `This permanently deletes ${confirm.school.name} and cannot be undone.`
    : confirm ? `Delete "${confirm.name}"? This cannot be undone.` : undefined;

  const rowBtn = 'inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-line-strong bg-surface text-slate hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40';

  const ready = !!schoolsQ.data;
  const activePct = totals.students > 0 ? Math.round((totals.active / totals.students) * 100) : null;
  const subtitle = ready
    ? `${plural(schools.length, 'institute', 'institutes', num(schools.length))}${cityCount > 0 ? ` in ${plural(cityCount, 'city', 'cities', num(cityCount))}` : ''} · ${plural(totals.students, 'student', 'students', num(totals.students))} · ${plural(totals.admins, 'school admin', 'school admins', num(totals.admins))}.`
    : undefined;

  const openDelete = (s: AdminSchool) => { setDeleteError(null); setConfirm({ kind: 'school', school: s }); };

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Institutes"
        title="Schools"
        subtitle={subtitle}
        actions={(
          <>
            <Segmented
              label="View"
              value={view}
              onChange={setView}
              options={[{ key: 'cards', label: 'Cards' }, { key: 'table', label: 'Table' }]}
            />
            <Button variant="ghost" icon={<Upload size={16} aria-hidden />} onClick={() => fileInputRef.current?.click()}>Import JSON</Button>
            <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".json" className="hidden" aria-label="Upload JSON file" />
            <Button icon={<Plus size={16} aria-hidden />} onClick={focusAddForm}>Add school</Button>
          </>
        )}
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[18px] min-[1181px]:grid-cols-4">
        <KpiCard
          label="Institutes"
          value={ready ? num(schools.length) : '—'}
          sub={ready ? `${num(schools.length - totals.setup)} active · ${num(totals.setup)} setting up` : undefined}
        />
        <KpiCard
          label="On Premium"
          value={ready ? <>{num(totals.premium)}<span className="text-[15px] text-muted"> / {num(schools.length)}</span></> : '—'}
          sub={ready ? `${num(totals.standard)} Standard · ${num(totals.pilot)} Pilot` : undefined}
        />
        <KpiCard
          label="Avg weekly activity"
          value={ready && activePct !== null ? <>{activePct}<span className="text-[15px]">%</span></> : '—'}
          sub={ready ? 'of enrolled students' : undefined}
        />
        <KpiCard
          label="Low activity"
          chip={ready ? `below ${LOW_ACTIVITY}%` : undefined}
          value={ready ? num(totals.low) : '—'}
          sub={ready ? (totals.low > 0 && lowest ? `${lowest.name} leads the list` : `institutes under ${LOW_ACTIVITY}% active`) : undefined}
          tone={ready && totals.low > 0 ? 'danger' : 'default'}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <Segmented label="Category" value={category} onChange={setCategory} options={CATEGORIES} />
        <label className="relative block max-w-[420px] flex-[1_1_240px]">
          <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            aria-label="Search institutes"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by institute name or category"
            className={`${INPUT} pl-10`}
          />
        </label>
        <div className="ml-auto flex min-w-0 max-w-full items-center gap-2 text-[13px] text-muted">
          <span aria-hidden="true">Sort</span>
          {/* A column-header sort outside these presets leaves no pill pressed. */}
          <Segmented
            dense
            scroll
            label="Sort institutes"
            value={sortValue}
            onChange={(v) => { const o = SORT_OPTIONS.find((x) => x.value === v); if (o) setSchoolSort({ key: o.key, dir: o.dir }); }}
            options={SORT_OPTIONS.map((o) => ({ key: o.value, label: o.label }))}
          />
        </div>
      </div>

      {deleteError && <div className="mb-4"><InlineError>{deleteError}</InlineError></div>}
      {adminFailure && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">
          <span className="min-w-0 flex-1">Added "{adminFailure.schoolName}", but {adminFailure.who} could not be assigned as administrator: {adminFailure.message}</span>
          <Button variant="danger-outline" size="sm" loading={adminRetrying} onClick={retryAssign}>Retry</Button>
          <Button variant="ghost" size="sm" onClick={() => setAdminFailure(null)}>Dismiss</Button>
        </div>
      )}
      {createdMsg && <p role="status" className="mb-4 rounded-[12px] bg-success-bg px-3 py-2 text-sm text-success">{createdMsg}</p>}

      {schoolsQ.isError && !schoolsQ.data ? (
        <PanelCard><ErrorPanel title="Couldn't load institutes" body={apiErrorText(schoolsQ.error, 'The server returned an error.')} onRetry={() => schoolsQ.refetch()} /></PanelCard>
      ) : schoolsQ.isLoading ? (
        <PanelCard><LoadingState label="Loading institutes…" /></PanelCard>
      ) : sortedSchools.length === 0 ? (
        <PanelCard>
          <EmptyState
            icon={<School size={20} />}
            title={search.trim() ? 'No institutes found' : category !== 'all' ? 'No institutes in this category yet' : 'No institutes yet'}
            body={search.trim() ? 'No institute matches this filter. Clear the search or pick another category.' : 'Add one with the Add school button.'}
          />
        </PanelCard>
      ) : view === 'cards' ? (
        <>
        <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-3">
          {sortedSchools.map((s) => {
            const r = activityRate(s);
            return (
            <article key={s.id} className="flex min-w-0 flex-col gap-4 rounded-[20px] border border-line bg-surface p-[22px] shadow-card">
              <div className="flex items-start gap-3">
                <span aria-hidden className="grid h-11 w-11 flex-none place-items-center rounded-[14px] bg-mist font-display text-sm font-bold text-navy dark:text-ink">{initials(s.name)}</span>
                <span className="min-w-0 flex-1">
                  <h3 className="truncate font-display text-base font-bold text-ink" title={s.name}>
                    <button type="button" onClick={() => openManage(s)} className="max-w-full truncate bg-transparent p-0 text-left font-[inherit] text-inherit hover:underline hover:underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">{s.name}</button>
                  </h3>
                  <span className="text-[13px] text-muted">{s.city ? `${s.city} · ${s.creator_type_name}` : s.creator_type_name}</span>
                </span>
                <PlanPill plan={s.plan} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {([['Students', s.student_count], ['Teachers', s.teacher_count], ['Admins', s.admin_count]] as const).map(([label, v]) => (
                  <div key={label} className="rounded-[12px] bg-surface-2 px-3 py-2.5">
                    <span className="block font-mono text-lg font-semibold text-ink">{num(v ?? 0)}</span>
                    <span className="text-[11.5px] text-muted">{label}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="mb-[7px] flex justify-between text-[13px]">
                  <span className="text-muted">Active this week</span>
                  <span className="font-mono font-semibold text-ink">{r === null ? '—' : `${Math.round(r)}%`}</span>
                </div>
                <Track label={`Active this week: ${s.name}`} value={r ?? 0} tone={r !== null && r < LOW_ACTIVITY ? 'accent' : 'slate'} />
              </div>
              <div className="mt-auto flex items-center gap-2 border-t border-hairline pt-3.5">
                <span className="flex-1 text-xs text-muted">{r === null && !s.last_activity ? 'Setup pending' : `Last activity ${lastSeen(s.last_activity)}`}</span>
                <Button variant="ghost" size="sm" aria-label={`Manage ${s.name}`} onClick={() => openManage(s)}>Manage</Button>
                <UiIconButton label={`Edit ${s.name}`} onClick={() => openEdit(s)} icon={<Pencil size={16} aria-hidden />} />
                <UiIconButton label={`Delete ${s.name}`} tone="danger" onClick={() => openDelete(s)} icon={<Trash2 size={16} aria-hidden />} />
              </div>
            </article>
            );
          })}
        </div>
        <PagedFooter className="mt-[18px]" from={(currentPage - 1) * PAGE_SIZE + 1} to={(currentPage - 1) * PAGE_SIZE + sortedSchools.length}
          total={total} page={currentPage} pageCount={totalPages} onPage={setPage} label="Institutes pagination" />
        </>
      ) : (
        <PanelCard>
          <Table aria-label="Institutes" minWidth={860}>
            <thead>
              <tr>
                <SortableTh columnKey="id" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>ID</SortableTh>
                <SortableTh columnKey="name" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Institute</SortableTh>
                <SortableTh columnKey="category" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Category</SortableTh>
                <SortableTh columnKey="plan" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Plan</SortableTh>
                <SortableTh columnKey="students" align="right" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Students</SortableTh>
                <SortableTh columnKey="admins" align="right" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Admins</SortableTh>
                <SortableTh columnKey="activity" className="w-[22%] border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Active this week</SortableTh>
                <SortableTh columnKey="last_activity" className="border-b border-line" sort={schoolSort} onToggle={toggleSchoolSort}>Last activity</SortableTh>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {sortedSchools.map((s) => (
                <tr key={s.id}>
                  <Td className="font-mono font-medium text-muted">{s.id}</Td>
                  <Td className="max-w-[260px]">
                    <span className="flex items-center gap-3">
                      <Initials name={s.name} />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-ink" title={s.name}>{s.name}</span>
                        {s.city && <span className="block truncate text-xs text-muted" data-testid="school-city">{s.city}</span>}
                      </span>
                    </span>
                  </Td>
                  <Td>{s.creator_type_name}</Td>
                  <Td><PlanPill plan={s.plan} /></Td>
                  <Td align="right" className="font-mono font-medium text-ink">{num(s.student_count ?? 0)}</Td>
                  <Td align="right" className="font-mono font-medium text-ink">{num(s.admin_count ?? 0)}</Td>
                  <Td><ActiveBar s={s} /></Td>
                  <Td className="whitespace-nowrap text-muted">{lastSeen(s.last_activity)}</Td>
                  <Td>
                    <div className="flex justify-end gap-1.5 whitespace-nowrap">
                      <UiIconButton label={`Manage ${s.name}`} onClick={() => openManage(s)} icon={<Settings size={16} aria-hidden />} />
                      <UiIconButton label={`Edit ${s.name}`} onClick={() => openEdit(s)} icon={<Pencil size={16} aria-hidden />} />
                      <UiIconButton label={`Delete ${s.name}`} tone="danger" onClick={() => openDelete(s)} icon={<Trash2 size={16} aria-hidden />} />
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <PagedFooter divider="list" from={(currentPage - 1) * PAGE_SIZE + 1} to={(currentPage - 1) * PAGE_SIZE + sortedSchools.length}
            total={total} page={currentPage} pageCount={totalPages} onPage={setPage} label="Institutes pagination" />
        </PanelCard>
      )}

      {/* Add school drawer */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        eyebrow="New institute"
        title="Add school"
        subtitle="Grades and subjects can be set up after it is created."
        side="right"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button type="submit" form="add-institute-form" loading={createMut.isPending} disabled={lookupBusy || !!confirmExisting}>
              {createMut.isPending ? 'Adding…' : 'Add institute'}
            </Button>
          </>
        )}
      >
        <form id="add-institute-form" onSubmit={onCreate} noValidate className="space-y-4">
          <FormField label="Institute name" htmlFor="new-institute-name" required error={formErrors.name}>
            <input
              id="new-institute-name"
              value={form.name}
              maxLength={isBulkName ? undefined : NAME_MAX}
              aria-invalid={!!formErrors.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setFormErrors((x) => ({ ...x, name: undefined })); setCreatedMsg(null); }}
              placeholder="e.g. Dire Dawa Sabian Secondary"
              className={INPUT}
            />
          </FormField>
          <div className="space-y-1.5">
            <span className="block text-[13px] font-semibold text-ink">Institute category <span className="text-danger-ink">*</span></span>
            <Segmented
              scroll
              label="Institute category"
              describedBy={formErrors.creator_type ? 'new-institute-category-error' : undefined}
              value={form.creator_type === '' ? '' : String(form.creator_type)}
              onChange={(k) => { setForm({ ...form, creator_type: Number(k) }); setFormErrors((x) => ({ ...x, creator_type: undefined })); setCreatedMsg(null); }}
              options={types.map((t: AdminCreatorType) => ({ key: String(t.id), label: t.name }))}
            />
            {formErrors.creator_type && <div id="new-institute-category-error"><InlineError>{formErrors.creator_type}</InlineError></div>}
          </div>
          <FormField label="City / region" htmlFor="new-institute-city">
            <CityCombobox id="new-institute-city" value={form.city} maxLength={NAME_MAX} onChange={(city) => setForm((f) => ({ ...f, city }))} placeholder="Start typing, e.g. Dire Dawa" className={INPUT} />
          </FormField>
          <div className="space-y-1.5">
            <span className="block text-[13px] font-semibold text-ink">Plan</span>
            <Segmented<AdminSchoolPlan> label="Plan" value={form.plan} onChange={(plan) => setForm({ ...form, plan })} options={PLAN_OPTIONS} />
            <p className="text-xs text-muted">{planNote(form.plan)}</p>
          </div>
          <FormField label="AI credit allocation override" htmlFor="new-institute-override" error={formErrors.override} hint="Optional. Blank uses the plan default.">
            <input id="new-institute-override" inputMode="numeric" value={form.override} aria-invalid={!!formErrors.override} onChange={(e) => { setForm({ ...form, override: e.target.value }); setFormErrors((x) => ({ ...x, override: undefined })); }} className={INPUT} />
          </FormField>
          {!isBulkName && (
            <fieldset disabled={lookupBusy || !!confirmExisting || createMut.isPending} aria-labelledby="first-admin-label" className="m-0 flex min-w-0 flex-col gap-3 rounded-[14px] border-0 bg-surface-2 p-3.5">
              <span id="first-admin-label" className="text-[13px] font-semibold text-ink">First administrator <span className="font-normal text-muted">(optional)</span></span>
              <div className="self-start">
                <Segmented<FirstAdminMode> label="Administrator source" value={firstAdmin.mode} onChange={(mode) => { setFirstAdmin({ ...firstAdmin, mode }); setFirstAdminError(null); }}
                  options={[{ key: 'existing', label: 'Use an existing account' }, { key: 'create', label: 'Create new admin' }]} />
              </div>
              {firstAdmin.mode === 'existing' ? (
                <FormField label="Username" htmlFor="first-admin-username" hint="You confirm the person and their current school before they are assigned.">
                  <input id="first-admin-username" value={firstAdmin.username} onChange={(e) => setFirstAdmin({ ...firstAdmin, username: e.target.value })} placeholder="Username of an existing account" className={INPUT} />
                </FormField>
              ) : (
                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                  <FormField label="First name" htmlFor="first-admin-first"><input id="first-admin-first" value={firstAdmin.first_name} onChange={(e) => setFirstAdmin({ ...firstAdmin, first_name: e.target.value })} className={INPUT} /></FormField>
                  <FormField label="Last name" htmlFor="first-admin-last"><input id="first-admin-last" value={firstAdmin.last_name} onChange={(e) => setFirstAdmin({ ...firstAdmin, last_name: e.target.value })} className={INPUT} /></FormField>
                  <FormField label="Username" htmlFor="first-admin-new-username"><input id="first-admin-new-username" value={firstAdmin.username} onChange={(e) => setFirstAdmin({ ...firstAdmin, username: e.target.value })} className={INPUT} /></FormField>
                  <FormField label="Email" htmlFor="first-admin-email"><input id="first-admin-email" type="email" value={firstAdmin.email} onChange={(e) => setFirstAdmin({ ...firstAdmin, email: e.target.value })} className={INPUT} /></FormField>
                  <FormField label="Temporary password" htmlFor="first-admin-password" hint={`At least ${MIN_PASSWORD} characters. Share it privately.`}><input id="first-admin-password" type="password" value={firstAdmin.password} onChange={(e) => setFirstAdmin({ ...firstAdmin, password: e.target.value })} placeholder={`Min ${MIN_PASSWORD} characters`} className={INPUT} /></FormField>
                </div>
              )}
              {firstAdminError && <InlineError>{firstAdminError}</InlineError>}
            </fieldset>
          )}
          {createMut.isError && <InlineError>{apiErrorText(createMut.error, 'Could not add the institute.')}</InlineError>}
          <div className="flex items-start gap-2.5 rounded-[14px] bg-note px-3.5 py-3 text-[13px] text-muted-2">
            <Upload size={16} aria-hidden className="mt-0.5 flex-none text-slate" />
            <span>
              Adding many schools?{' '}
              <button type="button" onClick={() => fileInputRef.current?.click()} className="font-semibold underline underline-offset-2 hover:text-ink">Upload a JSON list</button>
              {' '}to create them in one go.
              {uploadError && <span className="mt-1 block"><InlineError>{uploadError}</InlineError></span>}
              {isBulkName && <span className="mt-1 block text-xs text-muted">A JSON list is loaded and will be added as several institutes.</span>}
            </span>
          </div>
        </form>
      </Modal>

      {/* Manage institute modal */}
      <Modal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        size="xl"
        title={
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="break-words font-display text-lg font-bold text-ink">{selected?.name}</h2>
              <Badge tone="primary">{selected?.creator_type_name}</Badge>
              <span className="text-xs tabular-nums text-muted">ID #{selected?.id}</span>
            </div>
          </div>
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
            <a href="/admin/exams" className="inline-flex min-h-[36px] items-center gap-2 rounded-[12px] border border-navy bg-navy px-3 py-1 text-[13px] font-semibold text-on-navy hover:bg-navy-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              <FileText size={16} aria-hidden /> Manage exams
            </a>
          </>
        }
      >
        <div className="space-y-5">
          <Tabs
            value={activeTab}
            onChange={switchTab}
            items={[
              { key: 'students', label: 'Students' },
              { key: 'structure', label: isUniversity(selected) ? 'Field structure' : 'Grade structure' },
              { key: 'admins', label: 'Administrators' },
            ]}
          />

          {/* Students tab */}
          {activeTab === 'students' && (
            <section className="rounded-[20px] border border-line">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line p-4">
                <div className="relative w-full sm:w-72">
                  <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
                  <input
                    type="search"
                    aria-label="Search student names"
                    value={studSearch}
                    onChange={(e) => { setStudSearch(e.target.value); setStudPage(1); }}
                    placeholder="Search student names..."
                    className={`${INPUT} pl-10`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" aria-label="Previous students page" onClick={() => setStudPage((p) => Math.max(1, p - 1))} disabled={studPage <= 1} className={`${rowBtn} disabled:opacity-40`}><ChevronLeft size={16} aria-hidden /></button>
                  <span className="text-xs tabular-nums text-muted">Page {studPage}</span>
                  <button type="button" aria-label="Next students page" onClick={() => setStudPage((p) => p + 1)} disabled={(studentsQ.data?.results?.length ?? 0) < 10} className={`${rowBtn} disabled:opacity-40`}><ChevronRight size={16} aria-hidden /></button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table aria-label="Students" className="w-full min-w-[420px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-line text-xs">
                      <SortableTh columnKey="id" sort={studentSort} onToggle={toggleStudentSort}>Student ID</SortableTh>
                      <SortableTh columnKey="name" sort={studentSort} onToggle={toggleStudentSort}>Full name</SortableTh>
                      <SortableTh columnKey="grade" sort={studentSort} onToggle={toggleStudentSort}>Grade/Level</SortableTh>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {studentsQ.isLoading ? (
                      <tr><td colSpan={3}><LoadingState label="Loading students…" className="py-6" /></td></tr>
                    ) : studentsQ.isError ? (
                      <tr><td colSpan={3}><ErrorPanel title="Couldn't load students" body={apiErrorText(studentsQ.error, 'Could not load students.')} onRetry={() => studentsQ.refetch()} /></td></tr>
                    ) : sortedStudents.length === 0 ? (
                      <tr><td colSpan={3}><EmptyState compact icon={<Search size={20} />} title="No students found" body={studSearch.trim() ? 'No student matches your search.' : 'No students are enrolled yet.'} /></td></tr>
                    ) : sortedStudents.map((st) => (
                      <tr key={st.id}>
                        <td className="px-3 py-3 tabular-nums text-muted">#{st.id}</td>
                        <td className="break-words px-3 py-3 font-semibold text-ink">{st.name}</td>
                        <td className="px-3 py-3"><Badge tone="neutral">{st.grade}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Structure tab */}
          {activeTab === 'structure' && selected && !isUniversity(selected) && (
            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              <PanelCard title="Grade levels" size="compact" actions={<Badge tone="neutral">{grades.length} total</Badge>}>
                <form className="mb-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); addGrade(); }}>
                  <input
                    aria-label="New grade"
                    value={newGrade}
                    maxLength={NAME_MAX}
                    onChange={(e) => setNewGrade(e.target.value)}
                    placeholder="New grade (e.g. Grade 1)"
                    className={`${INPUT} flex-1`}
                  />
                  <Button type="submit" size="sm" disabled={!newGrade.trim()}>Add</Button>
                </form>
                {gradeActionError && <div className="mb-3"><InlineError>{gradeActionError}</InlineError></div>}
                {gradesError ? (
                  <InlineError>{gradesError}</InlineError>
                ) : grades.length === 0 ? (
                  <EmptyState compact icon={<School size={20} />} title="No grades yet" body="Add the first grade level above." />
                ) : (
                  <ul className="max-h-[40vh] space-y-2 overflow-y-auto">
                    {grades.map((g) => (
                      <li
                        key={g.id}
                        className={`flex items-center justify-between gap-2 rounded-[12px] border p-2 ${selectedGradeId === g.id ? 'border-accent bg-accent-bg' : 'border-line'}`}
                      >
                        {g.id === editingGradeId ? (
                          <input
                            autoFocus
                            aria-label={`Rename ${g.name}`}
                            value={editingGradeName}
                            maxLength={NAME_MAX}
                            onChange={(e) => setEditingGradeName(e.target.value)}
                            onBlur={() => renameGrade(g.id, g.name)}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingGradeId(null); }}
                            className={INPUT}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => selected && loadGradeSubjects(selected.id, g.id)}
                            aria-pressed={selectedGradeId === g.id}
                            className="min-w-0 flex-1 break-words rounded-md px-2 py-1.5 text-left text-sm font-medium text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                          >
                            {g.name}
                          </button>
                        )}
                        <div className="flex shrink-0 gap-1">
                          <IconButton label={`Rename ${g.name}`} onClick={() => { setEditingGradeId(g.id); setEditingGradeName(g.name); }}><Pencil size={16} aria-hidden /></IconButton>
                          <IconButton label={`Delete ${g.name}`} tone="danger" onClick={() => setConfirm({ kind: 'grade', id: g.id, name: g.name })}><Trash2 size={16} aria-hidden /></IconButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </PanelCard>

              <PanelCard title="Subjects" size="compact" actions={<Badge tone="neutral">{subjects.length} total</Badge>}>
                <form className="mb-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); addSubject(); }}>
                  <input
                    aria-label="New subject"
                    disabled={!selectedGradeId}
                    value={newSubject}
                    maxLength={NAME_MAX}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder={selectedGradeId ? 'New subject (e.g. Mathematics)' : 'Select a grade first'}
                    className={`${INPUT} flex-1`}
                  />
                  <Button type="submit" size="sm" disabled={!selectedGradeId || !newSubject.trim()}>Add</Button>
                </form>
                {!selectedGradeId && <p className="mb-3 text-xs text-muted">Select a grade level to view and add its subjects.</p>}
                {subjectActionError && <div className="mb-3"><InlineError>{subjectActionError}</InlineError></div>}
                {!selectedGradeId ? (
                  <EmptyState compact icon={<School size={20} />} title="No grade selected" body="Pick a grade level on the left." />
                ) : subjectsLoading ? (
                  <LoadingState label="Loading subjects…" className="py-6" />
                ) : subjectsError !== null ? (
                  <div role="alert">
                    <ErrorPanel
                      title="Couldn't load subjects — the server returned an error"
                      body={subjectsError || undefined}
                      onRetry={() => selected && loadGradeSubjects(selected.id, selectedGradeId)}
                    />
                  </div>
                ) : subjects.length === 0 ? (
                  <EmptyState compact icon={<School size={20} />} title="No subjects added yet" body="Add the first subject for this grade above." />
                ) : (
                  <ul className="max-h-[40vh] space-y-2 overflow-y-auto">
                    {subjects.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 rounded-[12px] border border-line p-2">
                        {s.id === editingSubjectId ? (
                          <input
                            autoFocus
                            aria-label={`Rename ${s.name}`}
                            value={editingSubjectName}
                            maxLength={NAME_MAX}
                            onChange={(e) => setEditingSubjectName(e.target.value)}
                            onBlur={() => renameSubject(s.id, s.name)}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingSubjectId(null); }}
                            className={INPUT}
                          />
                        ) : (
                          <span className="min-w-0 flex-1 break-words px-2 py-1.5 text-sm font-semibold text-ink">{s.name}</span>
                        )}
                        <div className="flex shrink-0 gap-1">
                          <IconButton label={`Rename ${s.name}`} onClick={() => { setEditingSubjectId(s.id); setEditingSubjectName(s.name); }}><Pencil size={16} aria-hidden /></IconButton>
                          <IconButton label={`Delete ${s.name}`} tone="danger" onClick={() => setConfirm({ kind: 'subject', id: s.id, name: s.name })}><Trash2 size={16} aria-hidden /></IconButton>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </PanelCard>
            </div>
          )}

          {activeTab === 'structure' && selected && isUniversity(selected) && (
            <div className="grid grid-cols-1 items-start gap-5 md:grid-cols-2">
              <PanelCard title="Fields of study" size="compact">
                {fieldsError ? <InlineError>{fieldsError}</InlineError> : fields.length === 0 ? (
                  <EmptyState compact icon={<School size={20} />} title="No fields of study" body="This institute has no fields yet." />
                ) : (
                  <ul className="space-y-2">
                    {fields.map((f) => (
                      <li key={f.id}>
                        <button
                          type="button"
                          aria-pressed={selectedFieldId === f.id}
                          onClick={async () => {
                            setSelectedFieldId(f.id);
                            setYearsError(null);
                            try { setYears(await adminGetFieldAcademicYears(selected.id, f.id)); }
                            catch (err) { setYears([]); setYearsError(apiErrorText(err, 'Could not load academic years.')); }
                          }}
                          className={`w-full break-words rounded-[12px] border p-3 text-left text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${selectedFieldId === f.id ? 'border-accent bg-accent-bg font-semibold' : 'border-line hover:bg-surface-2'}`}
                        >
                          {f.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </PanelCard>
              <PanelCard title="Academic years" size="compact">
                {yearsError ? <InlineError>{yearsError}</InlineError> : years.length === 0 ? (
                  <EmptyState compact icon={<School size={20} />} title={selectedFieldId ? 'No years defined' : 'No field selected'} body={selectedFieldId ? undefined : 'Select a field of study.'} />
                ) : (
                  <ul className="space-y-2">
                    {years.map((y) => (
                      <li key={y.id} className="break-words rounded-[12px] border border-line p-3 text-sm font-medium">{y.name}</li>
                    ))}
                  </ul>
                )}
              </PanelCard>
            </div>
          )}

          {/* Administrators tab */}
          {activeTab === 'admins' && selected && (
            <div className="space-y-5">
              <PanelCard title="Active administrators" size="compact" actions={<Badge tone="primary">{admins.length}</Badge>}>
                <div className="overflow-x-auto">
                  <table aria-label="Administrators" className="w-full min-w-[420px] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-line text-xs">
                        <SortableTh columnKey="user" sort={adminSort} onToggle={toggleAdminSort}>User</SortableTh>
                        <SortableTh columnKey="email" sort={adminSort} onToggle={toggleAdminSort}>Contact information</SortableTh>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line">
                      {sortedAdmins.map((a) => (
                        <tr key={a.id}>
                          <td className="px-3 py-3">
                            <div className="break-words font-semibold text-ink">{((a.first_name || '') + ' ' + (a.last_name || '')).trim() || a.username}</div>
                            <div className="text-xs tabular-nums text-muted">ID: #{a.id}</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="break-all text-sm">{a.email || '--'}</div>
                            <div className="text-xs text-muted">Institute administrator</div>
                          </td>
                        </tr>
                      ))}
                      {admins.length === 0 && (
                        <tr><td colSpan={2}><EmptyState compact icon={<UserPlus size={20} />} title="No administrators assigned" body="Link an existing user or create one below." /></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PanelCard>

              <PanelCard title="Assign new administrator" size="compact">
                <p className="mb-4 text-sm text-muted">Link an existing system user or create a new account for this institute.</p>
                <div className="space-y-5">
                  <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={(e) => { e.preventDefault(); void linkExistingAdmin(); }}>
                    <div className="flex-1">
                      <FormField label="Link an existing user" htmlFor="admin-existing-user" hint="Username of a user who already has an account.">
                        <input id="admin-existing-user" value={adminForm.existing_user_id} onChange={(e) => setAdminForm({ ...adminForm, existing_user_id: e.target.value })} placeholder="Username" className={INPUT} />
                      </FormField>
                    </div>
                    <Button type="submit" variant="ghost" disabled={!adminForm.existing_user_id.trim()}>Link user</Button>
                  </form>

                  <form onSubmit={createAdmin} noValidate className="space-y-4 border-t border-line pt-4">
                    <h4 className="text-sm font-semibold text-ink">Create a new administrator</h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <FormField label="Username" htmlFor="admin-username" required>
                        <input id="admin-username" value={adminForm.username} onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })} className={INPUT} autoComplete="off" />
                      </FormField>
                      <FormField label="Password" htmlFor="admin-password">
                        <input id="admin-password" type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} className={INPUT} autoComplete="new-password" />
                      </FormField>
                      <FormField label="Email" htmlFor="admin-email">
                        <input id="admin-email" type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} className={INPUT} />
                      </FormField>
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="First name" htmlFor="admin-first">
                          <input id="admin-first" value={adminForm.first_name} onChange={(e) => setAdminForm({ ...adminForm, first_name: e.target.value })} className={INPUT} />
                        </FormField>
                        <FormField label="Last name" htmlFor="admin-last">
                          <input id="admin-last" value={adminForm.last_name} onChange={(e) => setAdminForm({ ...adminForm, last_name: e.target.value })} className={INPUT} />
                        </FormField>
                      </div>
                    </div>
                    {adminError && <InlineError>{adminError}</InlineError>}
                    {adminMsg && <p role="status" className="rounded-[12px] bg-success-bg px-3 py-2 text-sm text-success">{adminMsg}</p>}
                    <div className="flex justify-end border-t border-line pt-4">
                      <Button type="submit" icon={<UserPlus size={16} aria-hidden />}>Create and assign administrator</Button>
                    </div>
                  </form>
                </div>
              </PanelCard>
            </div>
          )}
        </div>
      </Modal>

      {/* Edit dialog */}
      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit institute"
        size="sm"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="sm" type="submit" form="edit-institute-form" loading={updateMut.isPending}>Save changes</Button>
          </>
        }
      >
        <form id="edit-institute-form" onSubmit={submitEdit} className="space-y-4">
          <FormField label="Institute name" htmlFor="edit-name" required>
            <input id="edit-name" value={editName} maxLength={NAME_MAX} onChange={(e) => setEditName(e.target.value)} className={INPUT} />
          </FormField>
          <div className="space-y-1.5">
            <span className="block text-xs font-medium text-bodydark2">Category</span>
            <Segmented
              scroll
              label="Institute category"
              value={editType === '' ? '' : String(editType)}
              onChange={(k) => setEditType(Number(k))}
              options={types.map((t: AdminCreatorType) => ({ key: String(t.id), label: t.name }))}
            />
          </div>
          <FormField label="City / region" htmlFor="edit-city">
            <CityCombobox id="edit-city" value={editCity} maxLength={NAME_MAX} onChange={setEditCity} className={INPUT} />
          </FormField>
          <div className="space-y-1.5">
            <span className="block text-xs font-medium text-bodydark2">Plan</span>
            <Segmented<AdminSchoolPlan> label="Plan" value={editPlan} onChange={setEditPlan} options={PLAN_OPTIONS} />
            <p className="text-xs text-muted">{planNote(editPlan)}</p>
          </div>
          <FormField label="AI credit allocation override" htmlFor="edit-override" error={editOverrideError ?? undefined} hint="Optional. Blank uses the plan default.">
            <input id="edit-override" inputMode="numeric" value={editOverride} aria-invalid={!!editOverrideError} onChange={(e) => { setEditOverride(e.target.value); setEditOverrideError(null); }} className={INPUT} />
          </FormField>
          {updateMut.isError && <InlineError>{apiErrorText(updateMut.error, 'Could not save the changes.')}</InlineError>}
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmExisting}
        title={confirmExisting ? `Make ${[confirmExisting.first_name, confirmExisting.last_name].filter(Boolean).join(' ') || confirmExisting.username} the administrator?` : ''}
        body={confirmExisting ? (confirmExisting.institution_name
          ? `${confirmExisting.username} is currently the administrator of ${confirmExisting.institution_name}. A person can run only one school, so assigning them here moves them to ${form.name.trim()}; ${confirmExisting.institution_name} may be left without an administrator.`
          : `${confirmExisting.username} doesn’t belong to a school yet. They will become the administrator of ${form.name.trim()}.`) : undefined}
        confirmLabel="Create school and assign"
        danger={false}
        onConfirm={() => {
          // Bind the request to the account the operator just confirmed: its id, not the typed text.
          if (confirmExisting) boundAdminRef.current = { payload: { user_id: confirmExisting.id, confirm_move: true }, who: confirmExisting.username };
          setConfirmExisting(null);
          submitCreate();
        }}
        onCancel={() => setConfirmExisting(null)}
      />

      <ConfirmDialog
        isOpen={!!moveConfirm}
        title={moveConfirm && selected ? `Move ${moveConfirm.username} to ${selected.name}?` : ''}
        body={moveConfirm && selected ? `${moveConfirm.username} is the administrator of ${moveConfirm.from}. A person can run only one school, so they will stop being the administrator of ${moveConfirm.from}.` : undefined}
        confirmLabel="Move administrator"
        danger
        onConfirm={() => { setMoveConfirm(null); void linkExistingAdmin(true); }}
        onCancel={() => setMoveConfirm(null)}
      />

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirmTitle}
        body={confirmBody}
        confirmLabel="Delete"
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
};

export default AdminSchools;
